import React, { useEffect, useState } from 'react';
import { useBooking, StaffMember } from '../../contexts/booking/BookingContext';
import { supabase } from '../../lib/supabaseClient';

const StaffSelection: React.FC = () => {
  const { bookingState, setSelectedStaff, nextStep, prevStep } = useBooking();
  const { selectedServices, selectedStaff } = bookingState;
  
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch staff members
  useEffect(() => {
    const fetchStaffMembers = async () => {
      try {
        setIsLoading(true);

        // Get all active staff members
        const { data, error } = await supabase
          .from('team')
          .select('*')
          .eq('is_active', true)
          .order('display_order');

        if (error) {
          throw new Error(`Error fetching staff: ${error.message}`);
        }

        setStaffMembers(data || []);
      } catch (err: any) {
        console.error('Error fetching staff data:', err);
        setError(err.message || 'An error occurred while fetching staff');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStaffMembers();
  }, []);

  // Handle staff selection
  const handleStaffSelection = (staff: StaffMember) => {
    setSelectedStaff(staff);
  };

  // Handle "Continue" button click
  const handleContinue = () => {
    nextStep();
  };

  // Helper functions to safely get numeric price/duration
  const getNumericPrice = (service: any): number => {
    if (typeof service.price === 'number') return service.price;
    if (typeof service.price === 'string') {
      const parsed = parseFloat(service.price);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };
  const getNumericDuration = (service: any): number => {
    if (typeof service.duration === 'number') return service.duration;
    if (typeof service.duration === 'string') {
      const parsed = parseInt(service.duration, 10);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B2D8C5]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="bg-[#B2D8C5] text-white px-4 py-2 rounded-md"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-serif text-[#232347] mb-2">Choose Your Therapist</h2>
      <p className="text-gray-600 mb-6">Select a staff member who will perform your service(s)</p>
      
      {/* Selected services reminder */}
      <div className="mb-6 bg-[#F1F7F4] p-4 rounded-md">
        <h3 className="text-lg font-medium text-[#232347] mb-2">Your Selected Services:</h3>
        <ul className="space-y-1">
          {selectedServices.map(service => (
            <li key={service.id} className="flex justify-between">
              <span>{service.name}</span>
              <span className="text-sm text-gray-600">${getNumericPrice(service).toFixed(2)} • {getNumericDuration(service)} min</span>
            </li>
          ))}
        </ul>
      </div>
      
      {/* Staff selection grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {staffMembers.map(staff => (
          <div 
            key={staff.id} 
            className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
              selectedStaff?.id === staff.id 
                ? 'border-[#B2D8C5] bg-[#F1F7F4]' 
                : 'border-gray-200 hover:border-[#B2D8C5]'
            }`}
            onClick={() => handleStaffSelection(staff)}
          >
            <div className="flex flex-col items-center">
              <div className="w-24 h-24 rounded-full overflow-hidden mb-4 border-2 border-[#B2D8C5]">
                <img
                  src={staff.photo || 'https://via.placeholder.com/150'}
                  alt={staff.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <h3 className="text-lg font-medium text-[#232347] mb-1">{staff.name}</h3>
              <p className="text-gray-600 text-sm">{staff.role}</p>
              
              {selectedStaff?.id === staff.id && (
                <div className="mt-4 text-[#B2D8C5]">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* No preference option */}
      <div 
        className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md mb-8 ${
          selectedStaff === null 
            ? 'border-[#B2D8C5] bg-[#F1F7F4]' 
            : 'border-gray-200 hover:border-[#B2D8C5]'
        }`}
        onClick={() => setSelectedStaff(null)}
      >
        <div className="flex items-center">
          <div className="bg-gray-200 rounded-full p-3 mr-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-medium text-[#232347]">No Preference</h3>
            <p className="text-gray-600 text-sm">We'll assign an available therapist for you</p>
          </div>
        </div>
      </div>
      
      {/* Navigation buttons */}
      <div className="mt-8 flex justify-between">
        <button
          onClick={prevStep}
          className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleContinue}
          className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default StaffSelection;
